// src/components/admin/mail/MailComposeModal.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Paperclip, Send, X } from 'lucide-react';
import {
    ComposeFormData,
    ComposeMode,
    MailAccount,
    MailFolderId,
    MailMessageDetail,
} from '../../../types/mail';
import { readError, SendPayload, sendMessage } from './mailApi';
import {
    AccountAccent,
    buildForwardBody,
    buildForwardSubject,
    buildQuotedBody,
    buildReplySubject,
    composeTitle,
    formatBytes,
    parseAddressList,
    stripSelfAddresses,
} from './mailUtils';
import MailConfirmDialog from './MailConfirmDialog';
import {
    BORDER,
    Button,
    ErrorText,
    FieldLabel,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalOverlay,
    Textarea,
} from './mail.styles';

/** One or more comma-separated addresses. */
const ADDRESS_LIST = /^\s*[^\s@,]+@[^\s@,]+\.[^\s@,]+\s*(,\s*[^\s@,]+@[^\s@,]+\.[^\s@,]+\s*)*$/;

const composeSchema = z.object({
    to: z
        .string()
        .min(1, 'Zadajte aspoň jedného príjemcu')
        .regex(ADDRESS_LIST, 'Neplatná emailová adresa (viac adries oddeľte čiarkou)'),
    cc: z.union([z.literal(''), z.string().regex(ADDRESS_LIST, 'Neplatná adresa v kópii')]),
    bcc: z.union([z.literal(''), z.string().regex(ADDRESS_LIST, 'Neplatná adresa v skrytej kópii')]),
    subject: z.string().min(1, 'Predmet je povinný').max(255, 'Predmet je príliš dlhý'),
    body: z.string().min(1, 'Správa nemôže byť prázdna').max(200000, 'Správa je príliš dlhá'),
});

const MAX_UPLOAD_BYTES = 20_000_000;

/**
 * The identity banner. Deliberately the highest-contrast element in the dialog after the
 * subject: the one mistake that must never happen is answering a patient from the personal
 * Gmail, or a private message from the practice address.
 */
const IdentityBanner = styled.div<{ $gradient: string }>`
    background: ${(p) => p.$gradient};
    color: #ffffff;
    padding: 1rem 2rem;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 0.9rem 1.2rem;
    }
`;

const BannerLabel = styled.div`
    font-size: 0.78rem;
    font-weight: 500;
    opacity: 0.85;
`;

const BannerAddress = styled.div`
    font-size: 1.05rem;
    font-weight: 700;
    word-break: break-all;
`;

const BannerMode = styled.div`
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 0.15rem;
`;

const Field = styled.div`
    margin-bottom: 0.85rem;
`;

const InlineToggle = styled.button`
    border: none;
    background: none;
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;

    &:hover {
        color: #990147;
    }
`;

const ToggleRow = styled.div`
    display: flex;
    gap: 0.4rem;
    justify-content: flex-end;
    margin-bottom: 0.4rem;
`;

const AttachRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-top: 0.9rem;
    padding-top: 0.9rem;
    border-top: 1px solid ${BORDER};
`;

const AttachButton = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.5rem 0.85rem;
    border: 1.5px solid ${BORDER};
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;

    &:hover {
        border-color: #990147;
        color: #990147;
    }

    input {
        display: none;
    }
`;

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.6rem;
    background: #f8fafc;
    border: 1px solid ${BORDER};
    border-radius: 8px;
    font-size: 0.78rem;
    color: #334155;
    max-width: 100%;

    button {
        border: none;
        background: none;
        color: #94a3b8;
        cursor: pointer;
        display: inline-flex;
        padding: 0;

        &:hover {
            color: #dc2626;
        }
    }
`;

const ChipName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
`;

const ProgressTrack = styled.div`
    width: 100%;
    height: 6px;
    background: #f1f5f9;
    border-radius: 20px;
    overflow: hidden;
    margin-top: 0.75rem;
`;

const ProgressFill = styled.div<{ $percent: number; $accent: string }>`
    width: ${(p) => p.$percent}%;
    height: 100%;
    background: ${(p) => p.$accent};
    transition: width 0.2s ease;
`;

const Hint = styled.p`
    margin: 0.5rem 0 0 0;
    font-size: 0.75rem;
    color: #94a3b8;
`;

const SendLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

const newRequestId = (): string => {
    const c: any = typeof crypto !== 'undefined' ? crypto : null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return 'r-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
};

interface Props {
    open: boolean;
    mode: ComposeMode;
    account: MailAccount;
    accent: AccountAccent;
    folder: MailFolderId;
    /** The message being replied to or forwarded; null for a new message. */
    source: MailMessageDetail | null;
    onClose: () => void;
    onSent: () => void;
}

const MailComposeModal: React.FC<Props> = ({
    open,
    mode,
    account,
    accent,
    folder,
    source,
    onClose,
    onSent,
}) => {
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState(0);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [showDiscard, setShowDiscard] = useState(false);
    const requestIdRef = useRef<string>(newRequestId());
    const bodyRef = useRef<HTMLTextAreaElement | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isDirty },
    } = useForm<ComposeFormData>({
        resolver: zodResolver(composeSchema),
        defaultValues: { to: '', cc: '', bcc: '', subject: '', body: '' },
    });

    const values = watch();
    const draftKey = `mail:draft:${account.id}:${mode}:${source ? source.uid : 'new'}`;

    const prefill = useMemo((): ComposeFormData => {
        if (mode === 'new' || !source) {
            return { to: '', cc: '', bcc: '', subject: '', body: '' };
        }
        if (mode === 'forward') {
            return {
                to: '',
                cc: '',
                bcc: '',
                subject: buildForwardSubject(source.subject),
                body: buildForwardBody(source),
            };
        }
        // reply / replyAll
        const primary = source.replyTo && source.replyTo.length > 0 ? source.replyTo : [source.from];
        const to = stripSelfAddresses(primary, account.address);
        const cc =
            mode === 'replyAll'
                ? stripSelfAddresses(source.to.concat(source.cc), account.address).filter(
                      (a) => !to.some((t) => t.address.toLowerCase() === a.address.toLowerCase())
                  )
                : [];

        return {
            to: to.map((a) => a.address).join(', '),
            cc: cc.map((a) => a.address).join(', '),
            bcc: '',
            subject: buildReplySubject(source.subject),
            body: buildQuotedBody(source),
        };
    }, [mode, source, account.address]);

    // Prefill, restoring a saved draft in preference to a fresh one.
    useEffect(() => {
        if (!open) return;
        requestIdRef.current = newRequestId();
        setAttachments([]);
        setProgress(0);

        const saved = window.localStorage.getItem(draftKey);
        if (saved) {
            try {
                reset(JSON.parse(saved));
                toast.success('Obnovili sme rozpísanú správu');
            } catch {
                reset(prefill);
            }
        } else {
            reset(prefill);
        }

        setShowCc(prefill.cc.length > 0);
        setShowBcc(false);

        window.setTimeout(() => {
            const el = bodyRef.current;
            if (el && mode !== 'new' && mode !== 'forward') {
                el.focus();
                el.setSelectionRange(0, 0); // caret above the quote, not below it
            }
        }, 60);
    }, [open, draftKey, prefill, reset, mode]);

    // Autosave. Written continuously — and crucially BEFORE the send request fires — because
    // the global 401 interceptor can log her out mid-send and take the modal with it, and
    // switching admin tabs unmounts this whole section.
    useEffect(() => {
        if (!open || !isDirty) return;
        const timer = window.setTimeout(() => {
            window.localStorage.setItem(draftKey, JSON.stringify(values));
        }, 1000);
        return () => window.clearTimeout(timer);
    }, [open, draftKey, values, isDirty]);

    useEffect(() => {
        if (!open || !isDirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [open, isDirty]);

    const requestClose = () => {
        if (sending) return;
        if (isDirty && (values.body || '').trim()) {
            setShowDiscard(true);
        } else {
            onClose();
        }
    };

    const discardDraft = () => {
        window.localStorage.removeItem(draftKey);
        setShowDiscard(false);
        setAttachments([]);
        reset({ to: '', cc: '', bcc: '', subject: '', body: '' });
        onClose();
    };

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                requestClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isDirty, values.body, sending]);

    const addFiles = (fileList: FileList | null) => {
        if (!fileList) return;
        const incoming: File[] = [];
        for (let i = 0; i < fileList.length; i++) {
            incoming.push(fileList[i]);
        }
        const next = attachments.concat(incoming);
        const total = next.reduce((sum, f) => sum + f.size, 0);
        if (total > MAX_UPLOAD_BYTES) {
            toast.error('Prílohy môžu mať spolu najviac 20 MB');
            return;
        }
        setAttachments(next);
    };

    const onSubmit = async (data: ComposeFormData) => {
        setSending(true);
        setProgress(0);

        // Persist before sending, not after.
        window.localStorage.setItem(draftKey, JSON.stringify(data));

        const ref =
            source && mode !== 'new'
                ? { folder, uid: source.uid, uidValidity: source.uidValidity }
                : null;

        const payload: SendPayload = {
            to: parseAddressList(data.to),
            cc: parseAddressList(data.cc),
            bcc: parseAddressList(data.bcc),
            subject: data.subject,
            body: data.body,
            replyTo: mode === 'reply' || mode === 'replyAll' ? ref : null,
            forward: mode === 'forward' ? ref : null,
            clientRequestId: requestIdRef.current,
        };

        try {
            const response = await sendMessage(account.id, payload, attachments, setProgress);

            window.localStorage.removeItem(draftKey);
            setAttachments([]);
            reset({ to: '', cc: '', bcc: '', subject: '', body: '' });

            if (response.appendedToSent) {
                toast.success('Email bol odoslaný');
            } else {
                // The mail WAS delivered; only the Sent copy failed. Saying "failed" here
                // would be wrong and would invite a duplicate send to a patient.
                toast.success('Email bol odoslaný (kópiu do Odoslaných sa nepodarilo uložiť)');
            }
            onSent();
        } catch (err: any) {
            toast.error(readError(err, 'Email sa nepodarilo odoslať'));
        } finally {
            setSending(false);
            setProgress(0);
        }
    };

    const onBodyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Ctrl/Cmd+Enter sends. A bare Enter never does — this is a message to a patient.
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleSubmit(onSubmit)();
        }
    };

    const { ref: bodyFieldRef, ...bodyField } = register('body');
    const attachedBytes = attachments.reduce((sum, f) => sum + f.size, 0);

    return (
        <>
            <AnimatePresence>
                {open && (
                    <ModalOverlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={requestClose}
                    >
                        <Modal
                            $accent={accent.solid}
                            initial={{ scale: 0.96, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.96, opacity: 0 }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            {/*
                              There is no sender selector anywhere in this form. `from` is
                              derived server-side from the account id, so sending from the
                              wrong mailbox is not possible, not merely discouraged.
                            */}
                            <IdentityBanner $gradient={accent.gradient}>
                                <BannerLabel>Odosielate z</BannerLabel>
                                <BannerAddress>{account.address}</BannerAddress>
                                <BannerMode>{composeTitle(mode)}</BannerMode>
                            </IdentityBanner>

                            <ModalBody>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <ToggleRow>
                                        {!showCc && (
                                            <InlineToggle type="button" onClick={() => setShowCc(true)}>
                                                + Kópia
                                            </InlineToggle>
                                        )}
                                        {!showBcc && (
                                            <InlineToggle type="button" onClick={() => setShowBcc(true)}>
                                                + Skrytá kópia
                                            </InlineToggle>
                                        )}
                                    </ToggleRow>

                                    <Field>
                                        <FieldLabel htmlFor="mail-to">Komu</FieldLabel>
                                        <Input
                                            id="mail-to"
                                            {...register('to')}
                                            placeholder="adresa@example.sk, dalsia@example.sk"
                                            autoFocus={mode === 'new' || mode === 'forward'}
                                        />
                                        {errors.to && <ErrorText>{errors.to.message}</ErrorText>}
                                    </Field>

                                    {showCc && (
                                        <Field>
                                            <FieldLabel htmlFor="mail-cc">Kópia</FieldLabel>
                                            <Input id="mail-cc" {...register('cc')} placeholder="adresa@example.sk" />
                                            {errors.cc && <ErrorText>{errors.cc.message}</ErrorText>}
                                        </Field>
                                    )}

                                    {showBcc && (
                                        <Field>
                                            <FieldLabel htmlFor="mail-bcc">Skrytá kópia</FieldLabel>
                                            <Input id="mail-bcc" {...register('bcc')} placeholder="adresa@example.sk" />
                                            {errors.bcc && <ErrorText>{errors.bcc.message}</ErrorText>}
                                        </Field>
                                    )}

                                    <Field>
                                        <FieldLabel htmlFor="mail-subject">Predmet</FieldLabel>
                                        <Input id="mail-subject" {...register('subject')} placeholder="Predmet správy" />
                                        {errors.subject && <ErrorText>{errors.subject.message}</ErrorText>}
                                    </Field>

                                    <Field>
                                        <FieldLabel htmlFor="mail-body">Správa</FieldLabel>
                                        <Textarea
                                            id="mail-body"
                                            {...bodyField}
                                            ref={(el: HTMLTextAreaElement | null) => {
                                                bodyFieldRef(el);
                                                bodyRef.current = el;
                                            }}
                                            onKeyDown={onBodyKeyDown}
                                            placeholder="Napíšte správu…"
                                        />
                                        {errors.body && <ErrorText>{errors.body.message}</ErrorText>}
                                    </Field>

                                    <AttachRow>
                                        <AttachButton>
                                            <Paperclip size={15} />
                                            Priložiť súbor
                                            <input
                                                type="file"
                                                multiple
                                                onChange={(e) => {
                                                    addFiles(e.target.files);
                                                    e.target.value = ''; // allow re-picking the same file
                                                }}
                                            />
                                        </AttachButton>

                                        {attachments.map((file, index) => (
                                            <Chip key={file.name + index}>
                                                <ChipName>{file.name}</ChipName>
                                                <span style={{ color: '#94a3b8' }}>{formatBytes(file.size)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setAttachments(attachments.filter((_, i) => i !== index))
                                                    }
                                                    aria-label={`Odstrániť ${file.name}`}
                                                >
                                                    <X size={13} />
                                                </button>
                                            </Chip>
                                        ))}

                                        {mode === 'forward' && source && source.attachments.length > 0 && (
                                            <Hint>
                                                Pôvodné prílohy ({source.attachments.length}) sa prepošlú automaticky.
                                            </Hint>
                                        )}
                                    </AttachRow>

                                    {attachedBytes > 0 && (
                                        <Hint>Spolu {formatBytes(attachedBytes)} z 20 MB</Hint>
                                    )}

                                    {sending && progress > 0 && (
                                        <ProgressTrack>
                                            <ProgressFill $percent={progress} $accent={accent.solid} />
                                        </ProgressTrack>
                                    )}

                                    <Hint>Tip: odoslať môžete aj klávesovou skratkou Ctrl/Cmd + Enter.</Hint>
                                </form>
                            </ModalBody>

                            <ModalFooter>
                                <Button
                                    type="button"
                                    $variant="secondary"
                                    onClick={requestClose}
                                    disabled={sending}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    Zrušiť
                                </Button>
                                {/* The address is in the button too: the last thing read before the click. */}
                                <Button
                                    type="button"
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={sending}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    <Send size={16} />
                                    <SendLabel>
                                        {sending ? 'Odosielam…' : `Odoslať z ${account.address}`}
                                    </SendLabel>
                                </Button>
                            </ModalFooter>
                        </Modal>
                    </ModalOverlay>
                )}
            </AnimatePresence>

            <MailConfirmDialog
                open={showDiscard}
                title="Zahodiť rozpísanú správu?"
                message="Text, ktorý ste napísali, sa neuloží."
                confirmLabel="Zahodiť"
                cancelLabel="Pokračovať v písaní"
                danger
                onConfirm={discardDraft}
                onCancel={() => setShowDiscard(false)}
            />
        </>
    );
};

export default MailComposeModal;
